import { getReportsInfoListMock } from "../../__mocks__/reportsList";
import { API_URL, isMocksEnabled } from "../../api/helpers";
import type { RequestApi } from "../../api/index";
import {
  REPORT_TYPES,
  type ReportSummaryApi,
  type ReportSummaryItemApi,
  type ReportsPendingReviewItem,
  type ReportsPendingReviewResponse,
} from "../../api/Reports";

export type MisconductListTabKey =
  | "in_process"
  | "in_progress"
  | "internal_qa"
  | "pending_review"
  | "reviewed"
  | "client_qa";

export function buildMisconductListsPath(
  tab: MisconductListTabKey,
  individual: boolean
): string {
  const base = `/lists/misconduct/${tab}`;
  return individual ? `${base}/individual` : base;
}

function toDate(value: string | Date | undefined): Date {
  if (value instanceof Date) return value;
  if (value == null || value === "") return new Date(0);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

function firstDateValue(
  primary: string | Date | undefined,
  fallback: string | Date | undefined
): string | Date | undefined {
  return primary === "" || primary == null ? fallback : primary;
}

/**
 * Maps ``ReportSummaryItem`` rows from ``GET /lists/misconduct/*`` into the
 * legacy ``ReportsPendingReviewResponse`` shape used by Pending Review / Reviewed UIs.
 */
export function mapReportSummaryToPendingReviewItems(
  summaries: ReportSummaryItemApi[]
): ReportsPendingReviewItem[] {
  return summaries.map((row) => {
    const is360 =
      row.report_type === REPORT_TYPES.THREE_SIXTY || row.report_type === "360";
    return {
      report_uuid: row.report_uuid,
      full_name: row.full_name,
      company_uuid: row.company_uuid,
      company_name: row.company_name ?? "",
      blueprint_uuid: row.blueprint_uuid,
      blueprint_name: row.blueprint_name,
      behavior_kit_uuid: row.behavior_kit_uuid,
      behavior_kit_name: row.behavior_kit_name,
      created_at: toDate(firstDateValue(row.report_date_added, row.created_at)),
      finished_at: toDate(firstDateValue(row.finish_at, row.created_at)),
      client_report_id: row.client_report_id ?? "",
      requested_by: row.requested_by ?? "",
      flags_count_payload: row.flags_count_payload,
      workflow_run_uuid: row.workflow_run_uuid,
      person_uuid: row.person_uuid,
      workflow_payload: row.step_payload,
      was_amend: row.was_amend ?? false,
      job_title: row.job_title ?? "",
      recur: row.recur,
      is_360: is360,
    };
  });
}

export function reportSummaryApiToPendingReviewResponse(
  api: ReportSummaryApi,
  page: number,
  pageSize: number
): ReportsPendingReviewResponse {
  return {
    items: mapReportSummaryToPendingReviewItems(api.report_summaries),
    total_items: api.total_rows,
    page,
    page_size: pageSize,
    message: api.message,
    timestamp: api.timestamp,
  };
}

export async function fetchMisconductListReportSummary(
  requestApi: RequestApi,
  args: {
    tab: MisconductListTabKey;
    individual: boolean;
    names: string;
    page: number;
    pageSize: number;
    errorsOnly?: boolean;
  }
): Promise<ReportSummaryApi> {
  if (isMocksEnabled()) {
    const mock = await getReportsInfoListMock();
    return mock;
  }
  const path = buildMisconductListsPath(args.tab, args.individual);
  const offset = (args.page - 1) * args.pageSize;
  const params: Record<string, string | number> = {
    offset,
    size: args.pageSize,
  };
  if (args.tab === "in_process" && args.errorsOnly) {
    params.errors_only = 1;
  }
  if (args.names.trim()) {
    params.names = args.names.trim();
  }
  const response = await requestApi.get(`${API_URL}${path}`, params);
  const body = response.data as ReportSummaryApi;
  body.timestamp = Date.now();
  return body;
}
